/**
 * Pack 8.A — Locale-aware formatting helpers backed by `Intl.*`.
 *
 * Centralising every date / time / number / currency formatter through this
 * module keeps two important guarantees in place:
 *
 *  1. The numbering system follows the active locale. Arabic (`ar-*`) renders
 *     in Arabic-Indic digits per draft #713; western locales stay in
 *     ASCII digits. We achieve this by appending the Unicode `-u-nu-arab`
 *     extension when the caller did not supply one explicitly.
 *  2. RTL detection lives next to the formatters so the React provider can
 *     toggle `document.documentElement.dir` without duplicating the locale
 *     parsing logic.
 *
 * Every helper accepts a `locale` argument so unit tests, server-side
 * renderers, and React components can all use the exact same code path.
 */

const RTL_BASE_LANGUAGES = new Set([
  "ar", // Arabic
  "he", // Hebrew
  "fa", // Persian / Farsi
  "ur", // Urdu
  "ps", // Pashto
  "sd", // Sindhi
  "yi", // Yiddish
  "iw", // Legacy Hebrew tag
  "ji", // Legacy Yiddish tag
]);

/** Pull the base language sub-tag from a BCP 47 locale (`ar-SA` ➜ `ar`). */
export function getBaseLanguage(locale: string | null | undefined): string {
  if (!locale) return "en";
  return locale.split(/[-_]/)[0]!.toLowerCase();
}

/** True when the locale's primary script is right-to-left. */
export function isRtlLocale(locale: string | null | undefined): boolean {
  return RTL_BASE_LANGUAGES.has(getBaseLanguage(locale));
}

/**
 * Return a locale tag with an explicit `-u-nu-arab` Unicode extension when
 * the caller asked for an Arabic locale and did not specify a numbering
 * system already. Other locales pass through unchanged.
 *
 * Browsers and Node 20+ already default `ar-SA` to Arabic-Indic digits in
 * most cases, but the explicit extension makes the contract deterministic
 * across runtimes (notably PDFKit's embedded text renderer relies on the
 * upstream `Intl` polyfill, which is not always Arab-numeral by default).
 */
export function withNumberingSystem(locale: string | null | undefined): string {
  const tag = locale && locale.length > 0 ? locale : "en";
  if (/-u-/.test(tag)) return tag;
  if (getBaseLanguage(tag) === "ar") return `${tag}-u-nu-arab`;
  return tag;
}

/**
 * Convert any ASCII digits inside `value` to their Arabic-Indic
 * counterparts (U+0660 – U+0669). Useful for callers that compose a
 * string out of pre-formatted parts (e.g. PDF header chips) and need a
 * single pass to convert any literal digits left in the template.
 */
export function toArabicIndicDigits(value: string): string {
  return value.replace(/[0-9]/g, (d) => String.fromCharCode(0x0660 + Number(d)));
}

function safeDate(value: Date | string | number): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DEFAULT_DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
};

const DEFAULT_TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
};

const DEFAULT_DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  ...DEFAULT_DATE_OPTS,
  ...DEFAULT_TIME_OPTS,
};

/** Format a date in the active locale (locale-aware numerals). */
export function formatDate(
  value: Date | string | number | null | undefined,
  locale: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (value === null || value === undefined || value === "") return "";
  const d = safeDate(value);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat(withNumberingSystem(locale), opts ?? DEFAULT_DATE_OPTS).format(d);
  } catch {
    return d.toISOString();
  }
}

export function formatTime(
  value: Date | string | number | null | undefined,
  locale: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (value === null || value === undefined || value === "") return "";
  const d = safeDate(value);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat(withNumberingSystem(locale), opts ?? DEFAULT_TIME_OPTS).format(d);
  } catch {
    return d.toISOString();
  }
}

export function formatDateTime(
  value: Date | string | number | null | undefined,
  locale: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (value === null || value === undefined || value === "") return "";
  const d = safeDate(value);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat(withNumberingSystem(locale), opts ?? DEFAULT_DATETIME_OPTS).format(d);
  } catch {
    return d.toISOString();
  }
}

export function formatNumber(
  value: number | null | undefined,
  locale: string,
  opts?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  try {
    return new Intl.NumberFormat(withNumberingSystem(locale), opts).format(value);
  } catch {
    return String(value);
  }
}

export function formatCurrency(
  value: number | null | undefined,
  locale: string,
  currency: string,
  opts?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  try {
    return new Intl.NumberFormat(withNumberingSystem(locale), {
      style: "currency",
      currency,
      ...opts,
    }).format(value);
  } catch {
    return `${currency} ${value}`;
  }
}

export function formatPercent(
  value: number | null | undefined,
  locale: string,
  opts?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  try {
    return new Intl.NumberFormat(withNumberingSystem(locale), {
      style: "percent",
      maximumFractionDigits: 1,
      ...opts,
    }).format(value);
  } catch {
    return `${value}`;
  }
}

export function formatRelativeTime(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  locale: string,
  opts?: Intl.RelativeTimeFormatOptions,
): string {
  try {
    return new Intl.RelativeTimeFormat(withNumberingSystem(locale), opts ?? { numeric: "auto" }).format(value, unit);
  } catch {
    return `${value} ${unit}`;
  }
}
